/**
 * uploadImage.ts
 *
 * Shared People Hub image-upload helper — same POST /common/images/upload flow used by
 * the Observation KFA image uploader (see RoutineObservation/ObsImageUploadModal.tsx).
 */
import { Platform } from 'react-native';
import { createApiClient } from '../../../../api/apiClient';

const isWeb = Platform.OS === 'web';

export interface UploadedImage {
  imageUrl: string;
  fileName: string;
}

/** Converts a base64 `data:image/...` URI into a File (web) for multipart upload. */
function dataUrlToFile(dataUrl: string, fileName: string): File {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/data:(.*);base64/)?.[1] ?? 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], fileName, { type: mime });
}

// Uploads a single image to blob storage; the returned imageUrl is what gets persisted
// in the activity-completion payload. Accepts a base64 `data:` URI (what SignatureCanvas
// and the KFA camera/gallery pickers both produce).
export async function uploadDataUrlImage(
  dataUrl: string,
  fileName: string,
): Promise<UploadedImage | null> {
  const fileForUpload = isWeb
    ? dataUrlToFile(dataUrl, fileName)
    : { uri: dataUrl, name: fileName, type: 'image/png' };

  const formData = new FormData();
  formData.append('image', fileForUpload as any);
  const api = createApiClient();
  const res = await api.post('/common/images/upload', formData, {
    headers: { Accept: 'application/json' },
  });
  if (res?.data?.success && res.data.data?.imageUrl) {
    return { imageUrl: res.data.data.imageUrl, fileName: res.data.data.fileName };
  }
  return null;
}
